import { Route, Redirect, Switch, useHistory, useLocation } from 'react-router-dom';
import React, { Suspense, useCallback, useEffect, useState, lazy } from 'react';
import Loader from 'react-loaders';
import { useSelector } from 'react-redux';
import { assetDrawerUrl } from './platform/dashboards/components/assetDrawer/deepLink';
import { importRetryReload } from './utilities/importRetryReload';
import i18n from './utilities/i18n';
import { hideSplash } from '../capacitor/splash';
import { consentReturnUrl } from './landing/ducks';

const Platform = lazy(() => importRetryReload(() => import('./platform')));
const Login = lazy(() => importRetryReload(() => import('./landing/Login')));
const ForgotPassword = lazy(() => importRetryReload(() => import('./landing/ForgotPassword')));
const RecoverPassword = lazy(() => importRetryReload(() => import('./landing/NewPassword')));

// importRetryReload like every other lazy route: this provider wraps the whole authenticated
// switch, so a stale-chunk rejection without the retry/reload path would blank the app after a
// deploy.
const ChatAppProvider = lazy(() =>
    importRetryReload(() => import('../chat/app/ChatAppProvider')).then((m) => ({
        default: m.ChatAppProvider,
    })),
);
// eslint-disable-next-line no-unused-vars -- the new-message toaster mount is commented out below, kept for re-enabling
const CrossConversationToasterMount = lazy(() =>
    importRetryReload(() => import('../chat/ui/notifications/CrossConversationToaster')).then((m) => ({
        default: m.CrossConversationToasterMount,
    })),
);
// The literal comparisons inside an if-statement let webpack prune the dynamic import from a
// build without forwarding.
let ForwardDeliveryProvider = ({ children }) => children;
if (
    process.env.REACT_APP_CHAT_MULTISELECT_ENABLED === 'true' &&
    process.env.REACT_APP_CHAT_FORWARD_ENABLED === 'true'
) {
    ForwardDeliveryProvider = lazy(() =>
        importRetryReload(() => import('../chat/ui/picker/ForwardDelivery')).then((m) => ({
            default: m.ForwardDeliveryProvider,
        })),
    );
}

const Notfound = () => <h1>Not found</h1>;

const AppMain = () => {
    const location = useLocation();
    const history = useHistory();

    // Path AND query string: a destination's parameters are most of the destination for a
    // deep link (the feed drawer's `?asset=&event=&tab=`), so tracking the pathname alone
    // hands the user back a bare route that renders nothing.
    const fullPath = `${location.pathname}${location.search}`;
    const [previousPath, setPreviousPath] = useState('/');
    const [currentPath, setCurrentPath] = useState(fullPath);

    const isAuthenticated = useSelector((state) => state.authReducers.authenticated);
    const isWaitingAuthentication = useSelector((state) => state.authReducers.waiting_authentication);

    // The login screen is a first screen; the platform shell hides the splash itself once it renders.
    useEffect(() => {
        if (!isWaitingAuthentication && !isAuthenticated) hideSplash();
    }, [isWaitingAuthentication, isAuthenticated]);

    useEffect(() => {
        if (fullPath !== currentPath) {
            setPreviousPath(currentPath);
            setCurrentPath(fullPath);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- tracks previous location; recompute only when it changes — deps limited to the trigger; the other values it reads are read at fire time and intentionally excluded to avoid re-running on every render
    }, [fullPath]);

    // Where a new-message toast sends the user: the machine's Summary route, which opens its asset
    // drawer, landing on the Chat tab. The chat module has no route of its own, so the destination
    // is the shell's to decide. The conversation id rides along because a machine can carry more
    // than one thread, and the toast is about a specific one.
    // eslint-disable-next-line no-unused-vars -- only the commented-out toaster mount below reads it, kept for re-enabling
    const openChat = useCallback(
        (conversationId, nodeId) => history.push(assetDrawerUrl(nodeId, 'chat', conversationId)),
        [history],
    );

    return (
        <div>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="line-scale-party" />
                            </div>
                            <h6 className="mt-3">{i18n.t('Loading')}</h6>
                        </div>
                    </div>
                }
            >
                {isWaitingAuthentication ? (
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="line-scale-party" />
                            </div>
                            <h6 className="mt-3">{i18n.t('Authenticating')}</h6>
                        </div>
                    </div>
                ) : isAuthenticated ? (
                    <ChatAppProvider>
                        {/* <CrossConversationToasterMount onOpenConversation={openChat} /> */}
                        <ForwardDeliveryProvider>
                            <Switch>
                                {/* `state.from` is the destination the user was bounced off, carried
                                through the login by the redirect that bounced them. It beats
                                `previousPath`, which is a history tracker: that only holds the right
                                value when the bounce and the attempt land in separate commits, and
                                it stays as the fallback for every other way of reaching /login. */}
                                <Route
                                    exact
                                    path="/login"
                                    render={({ location: loginLocation }) => {
                                        // Opened by the MCP consent bridge: the JWT cookie is set, go back to it.
                                        const next = consentReturnUrl(loginLocation.search);
                                        if (next) {
                                            window.location.assign(next);
                                            return null;
                                        }
                                        return <Redirect to={loginLocation.state?.from ?? previousPath} />;
                                    }}
                                />
                                <Route path="/dashboard" component={Platform} />
                                <Route path="/" render={() => <Redirect to="/dashboard" />} />
                                <Route component={Notfound} />
                            </Switch>
                        </ForwardDeliveryProvider>
                    </ChatAppProvider>
                ) : (
                    <Switch>
                        <Route path="/login" exact component={Login} />
                        <Route path="/recover" exact component={RecoverPassword} />
                        {/* A push tap on a logged-out phone lands here. Carrying the attempted
                            location — query string included — is what lets the login hand it back
                            instead of dropping the person on a bare route. */}
                        <Route
                            path="/dashboard"
                            render={({ location: attempted }) => (
                                <Redirect to={{ pathname: '/login', state: { from: attempted } }} />
                            )}
                        />
                        <Route path="/forgot-password" exact component={ForgotPassword} />
                        <Route render={() => <Redirect to="/login" />} />
                    </Switch>
                )}
            </Suspense>
        </div>
    );
};

export default AppMain;
